import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PlayCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function IntegrityChecks() {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrity/checks/latest");
      setRun(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/integrity/checks/run");
      setRun(data);
      toast[data.overall_status === "PASS" ? "success" : "error"](
        data.overall_status === "PASS" ? "All integrity checks passed" : "Some integrity checks failed — see below"
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="integrity-checks-page">
      <PageHeader
        title="Integrity Checks"
        description="9 org-wide data-consistency checks — journal balance, period locks, stock ties, GRIR clearing, AR/AP control, and duplicate-posting guards. Runs nightly and on demand."
        action={
          <Button onClick={runNow} disabled={running} data-testid="run-integrity-checks-button">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Run now
          </Button>
        }
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !run ? (
        <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="integrity-checks-empty-state">
          No checks have run yet. Click "Run now" to audit the current data.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-5" data-testid="integrity-overall-card">
            {run.overall_status === "PASS" ? <CheckCircle2 className="h-6 w-6 text-emerald-500" /> : <XCircle className="h-6 w-6 text-rose-500" />}
            <div>
              <p className="text-sm font-semibold text-foreground">Overall: <StatusPill value={run.overall_status} /></p>
              <p className="mt-1 text-xs text-muted-foreground">Last run {fmtDateTime(run.run_at)} · {run.triggered_by === "SCHEDULED" ? "nightly scheduler" : "manual"}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {run.results.map((check) => (
              <div key={check.name} className="rounded-xl border border-border bg-card p-4" data-testid={`integrity-check-${check.name}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">{check.message}</p>
                  <StatusPill value={check.status} />
                </div>
                {check.status === "FAIL" && (
                  <p className="mt-1.5 text-xs text-rose-500" data-testid={`integrity-check-failures-${check.name}`}>
                    {check.failure_count} failing row(s)
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
